import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import {
  makeStyles,
  Popper,
  Fade,
  IconButton,
  Typography,
  CircularProgress,
  Divider
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import HistoryIcon from '@material-ui/icons/History'
import DenaliLogo from '../../../../apps/aiApp/Assistant/common/DenaliLogo'
import { useInvestigation } from './hooks/useInvestigation'
import { useResearchAgentEnabled } from './hooks/useResearchAgentEnabled'
import { useActiveInvestigation } from './hooks/useActiveInvestigation'
import InvestigationTimeline from './InvestigationTimeline'
import ReportView from './ReportView'
import QuestionsCard from './QuestionsCard'
import StuckView from './StuckView'
import SessionList from './SessionList'

const TRAY_WIDTH = 640
const POPPER_OFFSET_PX = 10
const TRAY_BOTTOM_MARGIN_PX = 12
const POPPER_Z = 1050
const TRAY_MIN_HEIGHT = 320

const useStyles = makeStyles(() => ({
  popper: {
    zIndex: POPPER_Z
  },
  panel: {
    width: TRAY_WIDTH,
    maxWidth: 'calc(100vw - 24px)',
    display: 'flex',
    flexDirection: 'column',
    background: 'linear-gradient(180deg, var(--color-ai-050) 0%, var(--opal-surface-default, #fff) 28%)',
    border: '1px solid var(--color-ai-300)',
    borderRadius: 10,
    boxShadow: '-8px 0 20px -8px rgba(15, 23, 42, 0.10)',
    overflow: 'hidden',
    position: 'relative',
    isolation: 'isolate'
  },
  panelBusy: {
    animation: '$panelPulse 2.4s ease-in-out infinite'
  },
  '@keyframes panelPulse': {
    '0%, 100%': {
      borderColor: 'var(--color-ai-300)',
      boxShadow:
        '-8px 0 20px -8px rgba(15, 23, 42, 0.10), ' +
        '0 0 14px -2px color-mix(in srgb, var(--color-ai-500) 25%, transparent)'
    },
    '50%': {
      borderColor: 'var(--color-ai-500)',
      boxShadow:
        '-8px 0 20px -8px rgba(15, 23, 42, 0.10), ' +
        '0 0 28px 0 color-mix(in srgb, var(--color-ai-500) 55%, transparent)'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    minHeight: 52
  },
  headerLogo: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 6,
    background: 'linear-gradient(135deg, var(--color-ai-500) 0%, var(--color-ai-600) 100%)',
    color: 'var(--color-contrast-ai-500)'
  },
  headerNav: {
    marginLeft: -4,
    color: 'var(--color-ai-700)'
  },
  headerText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column'
  },
  eyebrow: {
    fontSize: 10,
    fontWeight: 'var(--font-weight-bold, 700)',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
    color: 'var(--color-ai-700)'
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-neutral-900, #111827)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  headerSubject: {
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  divider: {
    backgroundColor: 'color-mix(in srgb, var(--color-ai-300) 60%, transparent)'
  },
  body: {
    flex: 1,
    overflow: 'auto',
    padding: 14,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    background: 'var(--opal-surface-default, #fff)'
  },
  centeredMessage: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: '0 24px',
    minHeight: 220,
    textAlign: 'center',
    fontSize: 13,
    color: 'var(--color-neutral-500, #6b7280)'
  },
  centeredMessageHeading: {
    fontSize: 13,
    fontWeight: 'var(--font-weight-semibold, 600)',
    color: 'var(--color-ai-700)'
  },
  errorMessage: {
    color: 'var(--color-danger-600, #c62828)',
    fontSize: 13
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 14px',
    minHeight: 32,
    fontSize: 11,
    color: 'var(--color-neutral-500, #6b7280)'
  },
  footerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12
  }
}))

const ResearchAgentTray = forwardRef(function ResearchAgentTray ({ anchorRef, open, onClose, clientId }, ref) {
  const classes = useStyles()
  const { enabled } = useResearchAgentEnabled()
  const { trackSession, isBusy } = useActiveInvestigation()

  const [mode, setMode] = useState('history')

  const investigation = useInvestigation()
  const {
    view,
    sessionId,
    start,
    openExisting,
    answerQuestions,
    continueRun,
    isActing
  } = investigation

  // skipNextDefaultMode prevents the open-effect from clobbering the
  // mode that startInvestigation just set. The new sessionId hasn't
  // arrived yet (start is async), so the heuristic would mis-fall to history.
  const wasOpenRef = useRef(open)
  const skipNextDefaultModeRef = useRef(false)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      if (!skipNextDefaultModeRef.current) {
        setMode(sessionId != null ? 'investigation' : 'history')
      }
      skipNextDefaultModeRef.current = false
    }
    wasOpenRef.current = open
  }, [open, sessionId])

  useImperativeHandle(ref, () => ({
    startInvestigation: async (bundle, subject) => {
      skipNextDefaultModeRef.current = true
      setMode('investigation')
      try {
        const newSessionId = await start(bundle, subject)
        if (newSessionId != null) trackSession(newSessionId)
      } catch (_) { /* surfaces via view */ }
    }
  }), [start, trackSession])

  const handleSelectSession = useCallback((session) => {
    openExisting(session.portfolioResearchReportSessionId)
    setMode('investigation')
  }, [openExisting])

  const handleBackToHistory = useCallback(() => setMode('history'), [])
  const handleClose = useCallback(() => onClose?.(), [onClose])

  const [panelHeight, setPanelHeight] = useState(null)
  useEffect(() => {
    if (!open || !anchorRef?.current) return undefined
    const compute = () => {
      const rect = anchorRef.current.getBoundingClientRect()
      const top = rect.bottom + POPPER_OFFSET_PX
      const available = window.innerHeight - top - TRAY_BOTTOM_MARGIN_PX
      setPanelHeight(Math.max(TRAY_MIN_HEIGHT, available))
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [open, anchorRef])

  if (!enabled) return null

  return (
    <Popper
      open={open}
      anchorEl={anchorRef?.current}
      placement='bottom-end'
      transition
      className={classes.popper}
      modifiers={{
        offset: { enabled: true, offset: `0, ${POPPER_OFFSET_PX}` },
        preventOverflow: { enabled: true, boundariesElement: 'viewport', padding: 8 },
        flip: { enabled: false }
      }}
    >
      {({ TransitionProps }) => (
        <Fade {...TransitionProps} timeout={160}>
          <div
            className={`${classes.panel} ${isBusy ? classes.panelBusy : ''}`}
            style={panelHeight != null ? { height: panelHeight } : undefined}
          >
            <TrayHeader
              mode={mode}
              view={view}
              onBack={handleBackToHistory}
              onClose={handleClose}
            />
            <Divider className={classes.divider} />
            <div className={classes.body}>
              {mode === 'history'
                ? <SessionList clientId={clientId} onSelect={handleSelectSession} />
                : (
                  <InvestigationBody
                    view={view}
                    sessionId={sessionId}
                    isActing={isActing}
                    onAnswerQuestions={answerQuestions}
                    onContinue={continueRun}
                  />
                )}
            </div>
            <Divider className={classes.divider} />
            <TrayFooter mode={mode} view={view} sessionId={sessionId} />
          </div>
        </Fade>
      )}
    </Popper>
  )
})

function TrayHeader ({ mode, view, onBack, onClose }) {
  const classes = useStyles()
  const isHistory = mode === 'history'
  const eyebrow = isHistory ? 'Recent investigations' : 'Investigating'
  const title = isHistory
    ? null
    : (view.kind === 'complete' && view.report?.title) || view.report?.report?.title || view.report?.subject?.rowName || 'Selected row'
  const subjectLine = !isHistory && view.report?.subject?.view && view.report?.subject?.asOfDate
    ? `${view.report.subject.view} · as of ${view.report.subject.asOfDate}`
    : null

  return (
    <div className={classes.header}>
      {isHistory
        ? (
          <span className={classes.headerLogo} aria-hidden='true'>
            <DenaliLogo size='16px' flat />
          </span>
        )
        : (
          <IconButton
            size='small'
            onClick={onBack}
            aria-label='Back to history'
            className={classes.headerNav}
          >
            <ArrowBackIcon fontSize='small' />
          </IconButton>
        )}
      <div className={classes.headerText}>
        <Typography component='div' className={classes.eyebrow}>{eyebrow}</Typography>
        {title && <Typography component='div' className={classes.headerTitle}>{title}</Typography>}
        {subjectLine && <Typography component='div' className={classes.headerSubject}>{subjectLine}</Typography>}
      </div>
      <IconButton size='small' onClick={onClose} aria-label='Close'>
        <CloseIcon fontSize='small' />
      </IconButton>
    </div>
  )
}

function InvestigationBody ({ view, sessionId, isActing, onAnswerQuestions, onContinue }) {
  const classes = useStyles()

  if (sessionId == null && view.kind === 'idle') {
    return (
      <div className={classes.centeredMessage}>
        <HistoryIcon fontSize='small' />
        <span className={classes.centeredMessageHeading}>No investigation selected</span>
        <span>Right-click a row in the grid to start a new investigation, or pick one from Recent investigations.</span>
      </div>
    )
  }

  switch (view.kind) {
    case 'idle':
      return (
        <div className={classes.centeredMessage}>
          <CircularProgress size={20} style={{ color: 'var(--color-ai-500)' }} />
          <span>Loading investigation&hellip;</span>
        </div>
      )
    case 'starting':
      return (
        <div className={classes.centeredMessage}>
          <CircularProgress size={20} style={{ color: 'var(--color-ai-500)' }} />
          <span>Starting investigation&hellip;</span>
        </div>
      )
    case 'start_failed':
      return (
        <div className={classes.centeredMessage}>
          <span className={classes.errorMessage}>Could not start the investigation.</span>
          <span>{view.error?.message || 'Unknown error'}</span>
        </div>
      )
    case 'investigating': {
      const steps = stepsFromReport(view.report)
      return <InvestigationTimeline steps={steps} cycle />
    }
    case 'needs_input': {
      const steps = stepsFromReport(view.report)
      const questions = view.report?.latestQuestions || view.report?.report?.questions || []
      return (
        <>
          <QuestionsCard
            questions={questions}
            isSubmitting={isActing}
            onSubmit={(answers) => onAnswerQuestions(answers)}
          />
          <InvestigationTimeline steps={steps} busyLabel='Waiting for your input&hellip;' />
        </>
      )
    }
    case 'complete':
      return <ReportView report={view.report} />
    case 'stuck':
      return (
        <>
          <StuckView
            status={view.report?.status}
            threadStatus={view.report?.threadStatus}
            onContinue={onContinue}
            isContinuing={isActing}
          />
          <ReportView report={view.report} />
        </>
      )
    case 'poll_failed':
      return (
        <div className={classes.centeredMessage}>
          <span className={classes.errorMessage}>Could not load the latest report state.</span>
          <span>{view.error?.message || 'Unknown error'}</span>
        </div>
      )
    default:
      return null
  }
}

function TrayFooter ({ mode, view, sessionId }) {
  const classes = useStyles()
  if (mode === 'history') {
    return <div className={classes.footer}><span>&nbsp;</span></div>
  }
  const report = view.report
  const evidenceCount = report?.evidenceCount
  const stepCount = stepsFromReport(report).length
  return (
    <div className={classes.footer}>
      <div className={classes.footerLeft}>
        {sessionId != null && <span>Session #{sessionId}</span>}
        {stepCount > 0 && <span>{stepCount} step{stepCount === 1 ? '' : 's'}</span>}
        {typeof evidenceCount === 'number' && evidenceCount > 0 && (
          <span>{evidenceCount} audit event{evidenceCount === 1 ? '' : 's'}</span>
        )}
      </div>
    </div>
  )
}

function stepsFromReport (report) {
  const inner = report?.report
  return Array.isArray(inner?.steps) ? inner.steps : []
}

const viewShape = PropTypes.shape({
  kind: PropTypes.string.isRequired,
  report: PropTypes.object,
  error: PropTypes.shape({ message: PropTypes.string })
})

ResearchAgentTray.propTypes = {
  anchorRef: PropTypes.shape({ current: PropTypes.any }),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  clientId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

TrayHeader.propTypes = {
  mode: PropTypes.oneOf(['history', 'investigation']).isRequired,
  view: viewShape.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
}

InvestigationBody.propTypes = {
  view: viewShape.isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isActing: PropTypes.bool,
  onAnswerQuestions: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired
}

TrayFooter.propTypes = {
  mode: PropTypes.oneOf(['history', 'investigation']).isRequired,
  view: viewShape.isRequired,
  sessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
}

export default ResearchAgentTray
